import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, ShieldCheck } from "@phosphor-icons/react";
import VerifyHelpDialog from "@/components/VerifyHelpDialog";

// Public verify page — reachable WITHOUT login. Anyone with the QR-scanned
// URL can see who signed the SPB, when, and the key SPB details. Server
// verifies the HMAC-signed token so no forgery is possible.
export default function VerifySignature() {
  const { token, sid, nonce } = useParams();
  const [state, setState] = useState({ loading: true, valid: null, data: null, error: "" });

  useEffect(() => {
    // Two entry points share this page:
    //   /verify-signature/:token  (legacy — HMAC token in URL, ~400 char QR)
    //   /v/:sid/:nonce            (new short-URL, ~68 char QR, printer-safe)
    const url = sid && nonce
      ? `${process.env.REACT_APP_BACKEND_URL}/api/public/v/${sid}/${nonce}`
      : `${process.env.REACT_APP_BACKEND_URL}/api/public/signature/${token}/verify`;
    axios.get(url)
      .then((r) => setState({ loading: false, valid: r.data.valid, data: r.data, error: "" }))
      .catch((err) => setState({ loading: false, valid: false, data: null,
                                 error: err?.response?.data?.detail || err.message }));
  }, [token, sid, nonce]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse font-mono text-muted-foreground">Memverifikasi tanda tangan…</div>
      </div>
    );
  }

  const p = state.data?.payload || {};
  const spb = state.data?.spb || {};
  const isValid = state.valid === true;

  return (
    <div className="min-h-screen bg-background flex items-start md:items-center justify-center p-4 md:p-8">
      <VerifyHelpDialog ready={!state.loading} />
      <div className="w-full max-w-2xl border-2 border-border bg-card rounded-xl overflow-hidden" data-testid="verify-signature-panel">
        <div className={`p-6 md:p-8 border-b-2 border-border flex items-center gap-4 ${isValid ? "bg-emerald-50" : "bg-destructive/10"}`}>
          {isValid ? (
            <CheckCircle size={56} weight="fill" className="text-emerald-600 shrink-0" />
          ) : (
            <XCircle size={56} weight="fill" className="text-destructive shrink-0" />
          )}
          <div>
            <div className="overline text-xs">Verifikasi Tanda Tangan SPB</div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight mt-1" data-testid="verify-status">
              {isValid ? "TANDA TANGAN VALID" : "TIDAK VALID"}
            </h1>
            {!isValid && (
              <p className="text-sm text-destructive mt-1" data-testid="verify-reason">
                {state.data?.reason || state.error || "Token tidak dapat diverifikasi"}
              </p>
            )}
          </div>
        </div>

        {/* Signer info always shown when we could decode the payload */}
        {p.signer && (
          <div className="p-6 md:p-8 border-b border-border">
            <div className="overline text-xs mb-3 flex items-center gap-2">
              <ShieldCheck size={14} /> Ditandatangani Oleh
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm font-mono">
              <span className="text-muted-foreground">Nama</span>
              <span className="font-bold" data-testid="verify-signer-name">{p.signer_name || "-"}</span>
              <span className="text-muted-foreground">Email</span>
              <span data-testid="verify-signer-email">{p.signer || "-"}</span>
              <span className="text-muted-foreground">Peran</span>
              <span className="uppercase">{p.role_label || p.signer_role || "-"}</span>
              <span className="text-muted-foreground">Ditandatangani</span>
              <span data-testid="verify-signed-at">{p.signed_at ? new Date(p.signed_at).toLocaleString("id-ID") : "-"}</span>
            </div>
          </div>
        )}

        {/* SPB snapshot when verify succeeded */}
        {isValid && spb.no_spb && (
          <div className="p-6 md:p-8">
            <div className="overline text-xs mb-3">Detail SPB</div>
            <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-sm font-mono">
              <span className="text-muted-foreground">No SPB</span>
              <span className="font-bold text-lg" data-testid="verify-no-spb">{spb.no_spb}</span>
              <span className="text-muted-foreground">Tanggal</span>
              <span>{spb.tanggal || "-"}</span>
              <span className="text-muted-foreground">No Polisi</span>
              <span>{spb.no_polisi || "-"}</span>
              <span className="text-muted-foreground">Produk</span>
              <span>{spb.produk_nama || "-"} {spb.jumlah ? `• ${spb.jumlah} ${spb.satuan || ""}` : ""}</span>
              <span className="text-muted-foreground">Distrik</span>
              <span>{spb.distrik_kode ? `${spb.distrik_kode} — ${spb.distrik_nama || ""}` : "-"}</span>
              <span className="text-muted-foreground">Pelanggan</span>
              <span>{spb.nama_pelanggan || "-"}</span>
              <span className="text-muted-foreground">Status</span>
              <span className="uppercase">{spb.status || "-"}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
